import Table from "common/Table/Table";
import { Payout, PayoutStatus } from "types/affiliation.types";
import React from "react";
import { ColumnsType } from "antd/es/table";
import { FROM_TABLET_COLUMN } from "common/NewLook/responsiveColumns.ts";
import Text from "common/Typography/Text";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import Status from "features/affiliation/common/Status";
import PayoutsActions from "features/affiliation/Payouts/PayoutsActions";
import { ActionsWrapper } from "features/affiliation/common/styles";
import {
  PAYOUTS_STATUS_DISPLAY_NAME,
  USD_PRECISION,
} from "features/affiliation/common/constants";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import { formatDate } from "@shared/utils/date";
import { toFixed } from "@shared/utils/numbers";
import { AnalyticsPageName, LanguageNameSpaces } from "@shared/types";
import { checkSearch } from "@shared/utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useTheme } from "styled-components";
import {
  formatUsd,
  NUMBER_COLUMN,
} from "features/affiliation/NewLook/newLookUtils";
import {
  CellFigure,
  CellText,
  DateCell,
} from "features/affiliation/NewLook/TablePanel";

interface PayoutsTableProps {
  payouts: Payout[] | undefined;
  loading: boolean;
  emptyStateComponent: React.JSX.Element;
  searchFilter?: string;
}

const PayoutsTable = ({
  payouts,
  loading,
  emptyStateComponent,
  searchFilter,
}: PayoutsTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.AFFILIATION);
  const { isNewLook } = useTheme();
  const filteredPayouts = payouts?.filter((payout) =>
    checkSearch(payout.payout_method, searchFilter)
  );

  const columns: ColumnsType<Payout> = [
    {
      title: t("payouts.created_date"),
      dataIndex: "time",
      showSorterTooltip: false,
      defaultSortOrder: "descend",
      sorter: (a, b) => sortByNumberProperty(a, b, "time"),
      render: (time) => <Text>{formatDate(time, true, true)}</Text>,
    },
    {
      title: t("payouts.method"),
      dataIndex: "payout_method",
      showSorterTooltip: false,
      sorter: (a, b) => sortByStringProperty(a, b, "payout_method"),
      render: (payout_method) => <Text>{payout_method}</Text>,
    },
    {
      title: t("payouts.amount"),
      dataIndex: "amount",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "amount"),
      render: (amount) =>
        isNewLook ? (
          formatUsd(amount)
        ) : (
          <Text>${toFixed(amount, USD_PRECISION)}</Text>
        ),
    },
    {
      title: (
        <div style={{ display: "flex" }}>
          {t("payouts.status")}
          <HelperTooltip
            analyticsFieldName={t("payouts.status")}
            analyticsPageName={AnalyticsPageName.AFFILIATION_PAYOUTS}
            description={
              <ul style={{ paddingInlineStart: 18, paddingBlockStart: 4 }}>
                <li>{t("payouts.status_tooltip.approved")}</li>
                <li>{t("payouts.status_tooltip.pending")}</li>
                <li>{t("payouts.status_tooltip.cancelled")}</li>
                <li>{t("payouts.status_tooltip.denied")}</li>
              </ul>
            }
          />
        </div>
      ),
      dataIndex: "status",
      showSorterTooltip: false,
      sorter: (a, b) => sortByNumberProperty(a, b, "status"),
      render: (status: PayoutStatus) => (
        <Status
          itemStatus={PayoutStatus}
          status={status}
          statusDisplayName={t(PAYOUTS_STATUS_DISPLAY_NAME[status])}
        />
      ),
    },
    {
      title: "",
      key: "action",
      dataIndex: "payout_id",
      width: 40,
      render: (payoutId, payout) => {
        if (payout.status === PayoutStatus.PENDING)
          return (
            <ActionsWrapper onClick={(e) => e.stopPropagation()}>
              <PayoutsActions payoutId={payoutId} />
            </ActionsWrapper>
          );
      },
    },
  ];

  // New look: the date over its time, the amount right-aligned.
  const newLookColumns: ColumnsType<Payout> = [
    { ...columns[0], render: (time) => <DateCell time={time} /> },
    {
      ...columns[1],
      ...FROM_TABLET_COLUMN,
      render: (payoutMethod) => <CellText $ink={true}>{payoutMethod}</CellText>,
    },
    {
      ...columns[2],
      ...NUMBER_COLUMN,
      render: (amount) => <CellFigure>{formatUsd(amount)}</CellFigure>,
    },
    columns[3],
    columns[4],
  ];

  const emptyState = React.cloneElement(emptyStateComponent, {
    isSearch: !!searchFilter?.length,
  });

  return (
    <Table<Payout>
      columns={isNewLook ? newLookColumns : columns}
      rowKey={(payout) => payout.payout_id}
      dataSource={filteredPayouts}
      loading={loading}
      locale={{ emptyText: emptyState }}
    />
  );
};

export default PayoutsTable;
